<template>
    <div v-if="shouldShow"
        class="gmf-comment-rating-summaries"
        :class="classes"
    >
        <div class="gmf-comment-rating-summary-avg">
            <div class="gmf-header gmf-mb-0">
            {{ summary.ratingAverage.toFixed(1) }}
            </div>
            <RatingStars :type="ratingType" :rating="summary.ratingAverage" />

            <template v-if="showRatingDisclaimer">
                <a class="gmf-text-xs gmf-comment-rating-basedon" href="#" @click.prevent="ratingDisclaimerOpen = true">
                    <span>{{ basedOnN }}</span>
                    <Icon icon="guideline-info"/>
                </a>
                <DropdownContent v-if="ratingDisclaimerOpen" @close="ratingDisclaimerOpen = false">
                    <div class="gmf-information-dropdown">
                        <div>{{ t('rating_disclaimer.text') }}</div>
                        <Link :href="reviewPolicyUrl" class="gmf-link" v-if="reviewPolicyUrl">{{ t('rating_disclaimer.read_more') }}</Link>
                        <template v-else-if="guidelines">
                            <a href="#" class="gmf-link" @click.prevent="reviewPolicyOpen = true">{{ t('rating_disclaimer.read_more') }}</a>
                            <Modal v-model="reviewPolicyOpen" :stackModals="true">
                                <div class="gmf-comment-guidelines-modal">
                                    <div class="gmf-header gmf-mb-4" v-if="guidelines.title" v-text="guidelines.title"></div>
                                    <div v-html="guidelines.text"></div>
                                </div>
                            </Modal>
                        </template>
                    </div>
                </DropdownContent>
            </template>
            <div class="gmf-text-xs gmf-comment-rating-basedon" v-else>
                <span>{{ basedOnN }}</span>
            </div>
        </div>
        <table v-if="!hideDistribution">
            <tbody>
            <tr v-for="stars in productScoreNumbers" :key="stars" class="gmf-comment-rating-summary-row">
                <td style="text-align: center;">{{ stars }}</td>
                <td style="width: 100%"><div class="gmf-comment-rating-bar"><div :style="{ width: (summary.distribution[stars].ratio*100)+'%' }"></div></div></td>
                <td style="text-align: right;">{{ (summary.distribution[stars].ratio*100).toFixed(0)+'%' }}</td>
            </tr>
            </tbody>
        </table>
        <slot />
    </div>
    <div v-if="shouldShow && ratingSummaries && ratingSummaries.perceived_size && ratingSummaries.perceived_size.ratingCount >= 3" style="max-width: 375px">
        <div class="gmf-subheader">{{ t('perceived_size.summary_title') }}</div>
        <div class="gmf-perceived-size-bar">
            <div class="gmf-perceived-size-bar-line-container" style="width: 37.5%">
                <div class="gmf-perceived-size-bar-line"></div>
                <div class="gmf-text-small">{{t('perceived_size.small')}}</div>
            </div>
            <div class="gmf-perceived-size-bar-line-container" style="width: 25%">
                <div class="gmf-perceived-size-bar-line"></div>
                <div class="gmf-text-small">{{t('perceived_size.normal')}}</div>
            </div>
            <div class="gmf-perceived-size-bar-line-container" style="width: 37.5%">
                <div class="gmf-perceived-size-bar-line"></div>
                <div class="gmf-text-small">{{t('perceived_size.large')}}</div>
            </div>
            <div
                class="gmf-perceived-size-bar-anchor" 
                :style="{
                    left: ((ratingSummaries.perceived_size.ratingAverage-1)/2)*100+'%'
                }"
            >
                <div class="gmf-perceived-size-bar-handle"></div>
            </div>
        </div>
        <template v-if="showPerceivedSizeText">
            <div class="gmf-perceived-size-text" v-if="ratingSummaries.perceived_size.ratingAverage < 1.75">
                {{ t('perceived_size.avg_small') }}
            </div>
            <div class="gmf-perceived-size-text" v-else-if="ratingSummaries.perceived_size.ratingAverage <= 2.25">
                {{ t('perceived_size.avg_normal') }}
            </div>
            <div class="gmf-perceived-size-text" v-else>
                {{ t('perceived_size.avg_large') }}
            </div>
        </template>
    </div>
</template>

<style>
.gmf-comment-rating-bar {
  background: var(--gmf-col-lightgray);
  height: 10px;
  margin-left: 12px;
  margin-right: 12px;
  border-radius: var(--gmf-rounding);
}

.gmf-comment-rating-bar div {
  height: 100%;
  background: var(--gmf-col-product_score);
  border-radius: var(--gmf-rounding);
}
.gmf-context .gmf-comment-rating-summaries table {
    width: unset;
    flex-grow: 1;
    max-width: 250px;
    min-width: 150px;
    flex-shrink: 0;
    margin: 0;
}

.gmf-comment-rating-summaries-hype_factor .gmf-comment-rating-bar div {
  background: var(--gmf-col-hype_factor);
}

.gmf-comment-rating-summary-row {
    white-space: nowrap;
}

.gmf-context .gmf-comment-rating-summary-row td {
  vertical-align: middle;
  line-height: 0;
  font-size: var(--gmf-text-small-font-size);
  padding-top: 3px;
  padding-bottom: 3px;
  border: none;
  word-break: unset;
}

.gmf-comment-rating-summary-star {
  width: 20px;
  height: 20px;
}
.gmf-context .gmf-comment-rating-summary-table-header {
  margin-bottom: 12px;
  font-size: 13px;
  margin-top: 22px;
}
.gmf-comment-rating-summaries {
  display: flex;
  gap: var(--gmf-p-5);

  align-items: center;
  flex-wrap: wrap;
}
.gmf-comment-rating-summary-avg {
  display: flex;
  gap: var(--gmf-p-1);
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
}
.gmf-comment-rating-summary-avg .gmf-rating-star {
  width: 20px;
  height: 20px;
}

/* Perceived size */
.gmf-perceived-size-bar {
    position: relative;
    margin-top: 8px;
    margin-bottom: 8px;
    display: flex;
}
.gmf-perceived-size-bar-anchor {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
}
.gmf-perceived-size-bar-handle {
    width: 16px;
    height: 16px;
    background: var(--gmf-col-black);
    border-radius: 50%;
    position: absolute;
    top: 7px;
    transform: translateX(-50%) translateY(-50%);
}

.gmf-perceived-size-bar-line-container {
    width: 30%;
    flex-grow: 1;
    text-align: center;
}
.gmf-perceived-size-bar-line {
    height: 4px;
    background: var(--gmf-col-gray);
    margin-top: 5px;
    margin-bottom: 5px;
}
.gmf-perceived-size-bar-line-container:first-child {
    text-align: left;
}
.gmf-perceived-size-bar-line-container:first-child .gmf-perceived-size-bar-line {
    border-radius: 99px 0 0 99px;
}
.gmf-perceived-size-bar-line-container:nth-child(3) {
    text-align: right;
}
.gmf-perceived-size-bar-line-container:nth-child(3) .gmf-perceived-size-bar-line {
    border-radius: 0 99px 99px 0;
}

.gmf-comment-rating-basedon {
    display: flex;
    align-items: center;
    gap: var(--gmf-p-2);
}
.gmf-comment-rating-basedon .gmf-icon {
    width: 15px;
    height: 15px;
}
.gmf-information-dropdown {
    padding: var(--gmf-p-4);
    background: var(--gmf-col-white);
    border-radius: var(--gmf-rounding);
    width: 300px;
    max-width: 100%;
    display: flex;
    flex-direction: column;
    gap: var(--gmf-p-2);
}

.gmf-context .gmf-perceived-size-text {
    margin-top: var(--gmf-p-4);
    background: var(--gmf-card-col);
    padding: var(--gmf-p-4);
    border-radius: var(--gmf-rounding);
    font-size: var(--gmf-text-small-font-size);
}
</style>

<script>
import advancedString from '@/utils/advancedString';
import state from '@/state';

import RatingStars from '@/components/RatingStars';
import Icon from '@/components/Icon';

import DropdownContent from '@/components/DropdownContent';
import Modal from '@/components/Modal';
import Link from '@/components/Link';
import md from '@/utils/md';

const t = state.t.prefix('comments');

export default {
    props: {
        ratingSummaries: {
            type: Object,
            required: true,
        },
        commentType: {
            type: String,
            required: true,
        },
        hideDistribution: {
            type: Boolean,
            default: false,
        },
    },
    data () {
        return {
            ratingDisclaimerOpen: false,
            reviewPolicyOpen: false,
        };
    },
    components: {
        RatingStars,
        Icon,
        DropdownContent,
        Modal,
        Link,
    },
    methods: {
        t,
    },
    computed: {
        showPerceivedSizeText () {
            return state.merchantConfig?.widgets?.commentSection?.perceivedSize?.showText;
        },
        reviewPolicyUrl () {
            return state.merchantConfig?.comments.guidelines?.product_review?.url;
        },
        guidelines () {
            if (! state.merchantConfig.commentForm?.showGuidelines) {
                return {};
            }
            const guidelines = {
                title: t('guidelines.product_review.title') || t('guidelines.default.title'),
                text: t('guidelines.product_review.text') || t('guidelines.default.text'),
            };

            if (guidelines.text) {
                guidelines.text = md(guidelines.text.replaceAll('\\n', '\n'));
            } else {
                return null;
            }

            return guidelines;
        },
        showRatingDisclaimer () {
            if (this.ratingType !== 'product_score') {
                return false;
            }
            return state.merchantConfig.widgets?.commentSection?.showRatingDisclaimer;
        },
        summary () {
            if (! this.ratingSummaries || !this.ratingSummaries[this.ratingType]) {
                if (! state.merchantConfig.widgets?.commentSection?.showEmptyRatingSummary) {
                    return null;
                }
                return {
                    ratingAverage: 0,
                    ratingCount: 0,
                    distribution: {
                        1: { ratio: 0 },
                        2: { ratio: 0 },
                        3: { ratio: 0 },
                        4: { ratio: 0 },
                        5: { ratio: 0 },
                    },
                };
            }
            return this.ratingSummaries[this.ratingType];
        },
        distribution () {
            return this.summary.distribution;
        },
        average () {
            return this.summary.ratingAverage;
        },
        classes () {
            const classes = {};
            classes['gmf-comment-rating-summaries-' + this.ratingType] = true;
            return classes;
        },
        basedOnN () {
            const ratingCount = this.summary.ratingCount;

            if (ratingCount > 1) {
                const p = advancedString(t('based_on_n_ratings.' + this.ratingType + '.plural'), { n: ratingCount });

                if (p) {
                    return p;
                }
            }

            return advancedString(t('based_on_n_ratings.' + this.ratingType + '.singular'), { n: ratingCount })
                || advancedString(t('based_on_n_ratings.' + this.ratingType + '.plural'), { n: ratingCount });
        },
        shouldShow () {
            if (!this.ratingType) {
                return false;
            }
            if (this.summary) {
                return true;
            }
            return false;
        },
        ratingType () {
            if (this.commentType === 'product_review') {
                return 'product_score';
            }
            if (this.commentType === 'product_hype') {
                return 'hype_factor';
            }
            if (this.commentType === 'site_review') {
                return 'site_score';
            }
            return null;
        },
        productScoreNumbers () {
            return [5,4,3,2,1]; // @TODO make dynamic
        },
    },
};
</script>